import { useCallback, useEffect, useRef, useState } from 'react';

import { apiService } from 'services/ApiService';
import { CANCELLATION_OUTCOME } from 'services/CancellationFunnel';

const START_WAIT_MS = 3000;
const newAttempt = () => ({ id: null, closed: false, pendingOutcome: null, ready: Promise.resolve() });
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const useCancellationAttempt = ({ isOpen, subscriptionId, entryPoint, step }) => {
  const attemptRef = useRef(newAttempt());
  const reportedStepRef = useRef(1);
  const stepRef = useRef(1);
  const [sessionKey, setSessionKey] = useState(0);
  stepRef.current = step;

  const settle = useCallback((attempt, outcome) => {
    if(attempt.closed){
      return;
    }
    if(!attempt.id){
      attempt.pendingOutcome = attempt.pendingOutcome ?? outcome;
      return;
    }
    attempt.closed = true;
    if(outcome === CANCELLATION_OUTCOME.confirmed){
      return;
    }
    apiService.closeCancellationAttempt({ attemptId: attempt.id, outcome }).catch((error) => console.error('Error:', error));
  }, []);

  const reportStep = useCallback((attempt, nextStep) => {
    if(!attempt.id || attempt.closed || nextStep <= reportedStepRef.current){
      return;
    }
    reportedStepRef.current = nextStep;
    apiService.advanceCancellationAttempt({ attemptId: attempt.id, step: nextStep }).catch((error) => console.error('Error:', error));
  }, []);

  useEffect(() => {
    if(!isOpen || !subscriptionId){
      return undefined;
    }

    const attempt = newAttempt();
    attemptRef.current = attempt;
    reportedStepRef.current = 1;
    attempt.ready = apiService.startCancellationAttempt({ subscriptionId, entryPoint })
      .then((response) => {
        const id = response?.data?.id ?? null;
        if(!id){
          console.error('Cancellation attempt was not recorded', response);
          return;
        }
        attempt.id = id;
        if(attempt.pendingOutcome){
          settle(attempt, attempt.pendingOutcome);
          return;
        }
        reportStep(attempt, stepRef.current);
      })
      .catch((error) => console.error('Error:', error));

    const abandonOnLeave = () => settle(attempt, CANCELLATION_OUTCOME.abandoned);
    const restoreFromCache = (event) => {
      if(event.persisted){
        setSessionKey((key) => key + 1);
      }
    };
    window.addEventListener('pagehide', abandonOnLeave);
    window.addEventListener('pageshow', restoreFromCache);

    return () => {
      window.removeEventListener('pagehide', abandonOnLeave);
      window.removeEventListener('pageshow', restoreFromCache);
      settle(attempt, CANCELLATION_OUTCOME.abandoned);
    };
  }, [isOpen, subscriptionId, entryPoint, sessionKey, settle, reportStep]);

  useEffect(() => {
    reportStep(attemptRef.current, step);
  }, [step, reportStep]);

  const markRetained = useCallback(() => settle(attemptRef.current, CANCELLATION_OUTCOME.retained), [settle]);
  const markConfirmed = useCallback(() => settle(attemptRef.current, CANCELLATION_OUTCOME.confirmed), [settle]);
  const leaveForPortal = useCallback(() => settle(attemptRef.current, CANCELLATION_OUTCOME.leftForPortal), [settle]);
  const knownAttemptId = useCallback(async () => {
    const attempt = attemptRef.current;
    await Promise.race([attempt.ready, wait(START_WAIT_MS)]);
    return attempt.id;
  }, []);

  return { markRetained, markConfirmed, leaveForPortal, knownAttemptId };
};
